import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, FileSignature, Receipt, Settings2 } from "lucide-react";
import { ContractDefaultsForm } from "@/components/contracts/ContractDefaultsForm";
import { NotificationSettingsCard } from "@/components/contracts/NotificationSettingsCard";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
export const CONTRACT_STATUS = { draft: "bg-slate-100 text-slate-700", sent: "bg-blue-100 text-blue-700", viewed: "bg-amber-100 text-amber-700", signed: "bg-green-100 text-green-700" };
export const INVOICE_STATUS = { draft: "bg-slate-100 text-slate-700", sent: "bg-blue-100 text-blue-700", partially_paid: "bg-amber-100 text-amber-700", paid: "bg-green-100 text-green-700" };

export default function ContractsPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([axios.get(`${API}/contracts`), axios.get(`${API}/invoices`)])
      .then(([c, i]) => { setContracts(c.data); setInvoices(i.data); })
      .catch(() => toast.error("Failed to load contracts"))
      .finally(() => setLoading(false));
  }, []);

  const outstanding = invoices.reduce((s, i) => s + Number(i.balance_due || 0), 0);
  const awaiting = contracts.filter(c => ["sent", "viewed"].includes(c.status)).length;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contracts-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}><FileSignature className="w-6 h-6 text-indigo-600" /> Contracts &amp; Invoices</h1>
        <p className="text-slate-600 mt-1">Convert accepted estimates into invoices and signed agreements. Start from a customer profile or the Estimates page.</p>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Stat label="Contracts signed" value={contracts.filter(c => c.status === "signed").length} testId="stat-signed" />
        <Stat label="Awaiting signature" value={awaiting} testId="stat-awaiting" highlight={awaiting > 0} />
        <Stat label="Outstanding balance" value={money(outstanding)} testId="stat-outstanding" />
      </div>

      <Tabs defaultValue="contracts">
        <TabsList className="w-full sm:w-auto grid grid-cols-3 sm:inline-flex h-auto">
          <TabsTrigger value="contracts" className="text-xs sm:text-sm" data-testid="tab-contracts">Contracts ({contracts.length})</TabsTrigger>
          <TabsTrigger value="invoices" className="text-xs sm:text-sm" data-testid="tab-invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="defaults" className="text-xs sm:text-sm" data-testid="tab-defaults"><Settings2 className="w-3.5 h-3.5 mr-1 hidden sm:inline" /> Contract Defaults</TabsTrigger>
        </TabsList>

        <TabsContent value="contracts" className="mt-4">
          <Card><CardContent className="pt-4 divide-y">
            {contracts.length === 0 && <Empty text="No contracts yet. Open a customer profile and click “Create Contract”." />}
            {contracts.map(c => (
              <div key={c.contract_id} className="flex items-center justify-between py-3 gap-3 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded" onClick={() => navigate(`/contracts/${c.contract_id}`)} data-testid={`contract-row-${c.contract_id}`}>
                <div className="min-w-0"><p className="font-medium text-sm">{c.contract_number} · {c.owner_name}</p><p className="text-xs text-slate-500 truncate">{c.property_address} · {c.invoice_number}</p></div>
                <div className="flex items-center gap-3 shrink-0"><span className="text-sm font-medium">{money(c.total)}</span><Badge className={CONTRACT_STATUS[c.status] || ""}>{c.status}</Badge></div>
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <Card><CardContent className="pt-4 divide-y">
            {invoices.length === 0 && <Empty text="No invoices yet. Convert an accepted estimate to get started." />}
            {invoices.map(i => (
              <div key={i.invoice_id} className="flex items-center justify-between py-3 gap-3 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded" onClick={() => navigate(`/invoices/${i.invoice_id}`)} data-testid={`invoice-row-${i.invoice_id}`}>
                <div className="min-w-0"><p className="font-medium text-sm flex items-center gap-1"><Receipt className="w-3.5 h-3.5 text-emerald-600" /> {i.invoice_number} · {i.customer_name}</p><p className="text-xs text-slate-500">From {i.estimate_number}{i.contract_id ? " · has contract" : ""}</p></div>
                <div className="text-right shrink-0"><p className="text-sm font-medium">{money(i.total)}</p><p className="text-xs text-slate-500">Balance {money(i.balance_due)}</p></div>
                <Badge className={INVOICE_STATUS[i.status] || ""}>{i.status.replace("_", " ")}</Badge>
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="defaults" className="mt-4 space-y-5"><NotificationSettingsCard isOwner={user?.role === "owner"} /><ContractDefaultsForm isOwner={user?.role === "owner"} /></TabsContent>
      </Tabs>
    </div>
  );
}

const Stat = ({ label, value, testId, highlight }) => (
  <Card data-testid={testId}><CardContent className="pt-5"><p className={`text-2xl font-bold ${highlight ? "text-amber-600" : "text-slate-900"}`}>{value}</p><p className="text-xs text-slate-500">{label}</p></CardContent></Card>
);
const Empty = ({ text }) => <p className="text-sm text-slate-500 py-8 text-center">{text}</p>;
